import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import {
  getGmailProfile,
  scanGmailPlacements,
  getGmailPlacementStats,
  getGmailScanHistory,
} from "@/lib/gmail-placement.functions";
import { triggerSeedInboxScan } from "@/lib/seed-scheduler.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Inbox, AlertTriangle, RefreshCw, Mail, Plus, Server, History } from "lucide-react";

export const Route = createFileRoute("/_authenticated/gmail-placement")({
  component: GmailPlacementPage,
});

const PLACEMENT_LABELS: Record<string, { label: string; color: string }> = {
  inbox: { label: "Inbox", color: "bg-emerald-500" },
  updates: { label: "Updates", color: "bg-sky-500" },
  social: { label: "Social", color: "bg-indigo-500" },
  promotions: { label: "Promotions", color: "bg-amber-500" },
  spam: { label: "Spam", color: "bg-rose-600" },
  other: { label: "Sonstige", color: "bg-zinc-500" },
};

function scoreColor(score: number | null): string {
  if (score == null) return "text-muted-foreground";
  if (score >= 85) return "text-emerald-500";
  if (score >= 65) return "text-sky-500";
  if (score >= 40) return "text-amber-500";
  return "text-rose-500";
}

function GmailPlacementPage() {
  const qc = useQueryClient();
  const profileFn = useServerFn(getGmailProfile);
  const statsFn = useServerFn(getGmailPlacementStats);
  const scanFn = useServerFn(scanGmailPlacements);
  const imapScanFn = useServerFn(triggerSeedInboxScan);
  const historyFn = useServerFn(getGmailScanHistory);

  const [windowHours, setWindowHours] = useState(168);
  const [lookbackHours, setLookbackHours] = useState(24);
  const [subjectTag, setSubjectTag] = useState("");
  const [maxMessages, setMaxMessages] = useState(50);

  const profile = useQuery({
    queryKey: ["gmail-profile"],
    queryFn: () => profileFn(),
    staleTime: 60_000,
  });

  const stats = useQuery({
    queryKey: ["gmail-placement-stats", windowHours],
    queryFn: () => statsFn({ data: { windowHours } }),
    refetchInterval: 30_000,
  });

  const history = useQuery({
    queryKey: ["gmail-scan-history", windowHours],
    queryFn: () => historyFn({ data: { windowHours, limitPerInbox: 20 } }),
    refetchInterval: 30_000,
  });

  const scan = useMutation({
    mutationFn: () =>
      scanFn({
        data: {
          lookbackHours,
          subjectTag: subjectTag.trim() || undefined,
          maxMessages,
        },
      }),
    onSuccess: (res) => {
      toast.success(
        `Scan abgeschlossen: ${res.scanned} Mails geprüft, ${res.inserted} gespeichert${
          res.errors ? ` (${res.errors} Fehler)` : ""
        }`,
      );
      qc.invalidateQueries({ queryKey: ["gmail-placement-stats"] });
      qc.invalidateQueries({ queryKey: ["gmail-scan-history"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Scan fehlgeschlagen"),
  });

  const imapScan = useMutation({
    mutationFn: () => imapScanFn(),
    onSuccess: () => {
      toast.success("IMAP-Scan für alle Gmail-Konten gestartet");
      qc.invalidateQueries({ queryKey: ["gmail-placement-stats"] });
      qc.invalidateQueries({ queryKey: ["gmail-scan-history"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "IMAP-Scan fehlgeschlagen"),
  });

  const profileError =
    profile.data && "ok" in profile.data && !profile.data.ok ? profile.data.error : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Gmail Placement Monitoring"
        description="Inbox-Platzierungsanalyse für beliebig viele Gmail-Konten. Das Hauptkonto wird per Gmail-API (Connector) gelesen, weitere Konten werden per IMAP + App-Passwort als Seed-Inbox angebunden."
      />

      <HelpPanel
        title="Hilfe: Landen meine Mails bei Gmail im Posteingang?"
        what={
          <>
            Diese Seite verbindet sich mit deinen <b>Gmail-Konten</b> und schaut nach: liegt die
            Test-Mail im Posteingang, in Werbung, oder im Spam?
          </>
        }
        why={
          <>
            Gmail sortiert sehr streng. Erst wenn du <b>regelmäßig</b> prüfst, weißt du, ob deine
            Versand-Strategie funktioniert.
          </>
        }
        how={[
          <>
            Haupt-Gmail-Konto verbinden (Knopf <b>Connect Gmail</b>) — sicherer OAuth-Login.
          </>,
          <>
            Weitere Konten per <b>App-Passwort</b> hinzufügen (in Gmail einmal generieren).
          </>,
          <>Tabelle zeigt für jedes Konto: Posteingang / Werbung / Spam pro Absender-Domain.</>,
          <>Rote Felder = dort landest du im Spam — Domain/Sender überprüfen.</>,
        ]}
        tips={[
          <>
            Mindestens 3 unabhängige Gmail-Konten testen, sonst ist das Ergebnis nicht
            aussagekräftig.
          </>,
          <>
            Werbung-Tab ist <b>nicht</b> Spam — aber auch nicht ideal. Posteingang ist das Ziel.
          </>,
        ]}
        glossary={[
          {
            term: "Placement",
            def: "Wo die Mail im Postfach landet (Posteingang, Werbung, Spam).",
          },
          {
            term: "IMAP",
            def: "Protokoll, um Mails von einem Server zu lesen. Brauchen wir, um die Seed-Postfächer auszuwerten.",
          },
          { term: "OAuth", def: "Sicherer Login bei Google ohne Passwort-Weitergabe." },
        ]}
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail className="size-4" /> Hauptkonto (Gmail-API Connector)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {profile.isLoading ? (
            <p className="text-sm text-muted-foreground">Lade…</p>
          ) : profileError ? (
            <div className="flex items-start gap-2 text-sm text-rose-500">
              <AlertTriangle className="size-4 mt-0.5" />
              <div>
                <p className="font-medium">Gmail-Verbindung fehlt oder ungültig</p>
                <p className="text-muted-foreground">{profileError}</p>
                <p className="text-muted-foreground mt-1">
                  Optional: ein Hauptkonto per Connector verbinden. Zusätzliche Gmail-Konten können
                  jederzeit als Seed-Inbox angelegt werden.
                </p>
              </div>
            </div>
          ) : profile.data && profile.data.ok ? (
            <div className="flex items-center gap-3">
              <Badge variant="secondary">{profile.data.emailAddress}</Badge>
              <span className="text-xs text-muted-foreground">
                {profile.data.messagesTotal.toLocaleString()} Mails im Postfach
              </span>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Server className="size-4" /> Weitere Gmail-Konten (IMAP)
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3 text-sm">
          <p className="text-muted-foreground flex-1 min-w-[240px]">
            Beliebig viele Gmail-Konten als Seed-Inbox mit Provider "Gmail" und einem App-Passwort
            hinzufügen — sie werden automatisch in dieser Übersicht ausgewertet.
          </p>
          <Button asChild variant="outline" size="sm">
            <Link to="/seed-inboxes">
              <Plus className="size-4 mr-1" /> Gmail-Konto hinzufügen
            </Link>
          </Button>
          <Button size="sm" onClick={() => imapScan.mutate()} disabled={imapScan.isPending}>
            <RefreshCw className={`size-4 mr-2 ${imapScan.isPending ? "animate-spin" : ""}`} />
            Alle Gmail-Konten jetzt per IMAP scannen
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Connector-Scan (nur Hauptkonto)</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-5">
          <div className="space-y-1">
            <Label>Zeitraum (Stunden)</Label>
            <Input
              type="number"
              min={1}
              max={168}
              value={lookbackHours}
              onChange={(e) => setLookbackHours(Number(e.target.value))}
            />
          </div>
          <div className="space-y-1">
            <Label>Max. Mails</Label>
            <Input
              type="number"
              min={1}
              max={200}
              value={maxMessages}
              onChange={(e) => setMaxMessages(Number(e.target.value))}
            />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label>Subject-Filter (optional)</Label>
            <Input
              value={subjectTag}
              onChange={(e) => setSubjectTag(e.target.value)}
              placeholder="z. B. [TEST-2024]"
            />
          </div>
          <div className="flex items-end">
            <Button
              className="w-full"
              onClick={() => scan.mutate()}
              disabled={
                scan.isPending || !profile.data || !("ok" in profile.data) || !profile.data.ok
              }
            >
              <RefreshCw className={`size-4 mr-2 ${scan.isPending ? "animate-spin" : ""}`} />
              Jetzt scannen
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Inbox className="size-4" /> Seed-Inboxes (Gmail)
          </CardTitle>
          <div className="flex items-center gap-2 text-xs">
            <Label className="text-xs">Fenster:</Label>
            <select
              className="bg-background border border-border rounded px-2 py-1"
              value={windowHours}
              onChange={(e) => setWindowHours(Number(e.target.value))}
            >
              <option value={24}>24h</option>
              <option value={72}>3 Tage</option>
              <option value={168}>7 Tage</option>
              <option value={720}>30 Tage</option>
            </select>
          </div>
        </CardHeader>
        <CardContent>
          {stats.isLoading ? (
            <p className="text-sm text-muted-foreground">Lade…</p>
          ) : !stats.data || stats.data.stats.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Noch keine Gmail-Konten angelegt. Lege unter "Seed-Inboxes" einen oder mehrere
              Einträge mit Provider "Gmail" an — jedes Konto erscheint hier mit eigenem
              Placement-Score.
            </p>
          ) : (
            <div className="space-y-4">
              {stats.data.stats.map((row) => {
                const buckets = Object.entries(row.counts).filter(([, v]) => v > 0);
                return (
                  <div key={row.inbox_id} className="rounded-lg border border-border p-4 space-y-3">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="font-medium">{row.display_name || row.email}</div>
                        <div className="text-xs text-muted-foreground">{row.email}</div>
                        {row.last_checked_at && (
                          <div className="text-[11px] text-muted-foreground mt-1">
                            Zuletzt gescannt: {new Date(row.last_checked_at).toLocaleString()}
                          </div>
                        )}
                        {row.last_error && (
                          <div className="text-[11px] text-rose-500 mt-1">{row.last_error}</div>
                        )}
                      </div>
                      <div className="text-right">
                        <div
                          className={`text-3xl font-semibold tabular-nums ${scoreColor(row.score)}`}
                        >
                          {row.score ?? "—"}
                        </div>
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          Placement-Score
                        </div>
                        <div className="text-[11px] text-muted-foreground">{row.total} Mails</div>
                      </div>
                    </div>

                    {row.total > 0 && (
                      <>
                        <div className="flex h-2 rounded-full overflow-hidden bg-muted">
                          {(
                            ["inbox", "updates", "social", "promotions", "other", "spam"] as const
                          ).map((k) => {
                            const v = row.counts[k];
                            if (!v) return null;
                            const pct = (v / row.total) * 100;
                            return (
                              <div
                                key={k}
                                className={PLACEMENT_LABELS[k].color}
                                style={{ width: `${pct}%` }}
                              />
                            );
                          })}
                        </div>
                        <div className="flex flex-wrap gap-3 text-xs">
                          {buckets.map(([k, v]) => (
                            <div key={k} className="flex items-center gap-1.5">
                              <span
                                className={`size-2 rounded-full ${PLACEMENT_LABELS[k]?.color ?? "bg-zinc-500"}`}
                              />
                              <span className="text-muted-foreground">
                                {PLACEMENT_LABELS[k]?.label ?? k}:
                              </span>
                              <span className="font-medium tabular-nums">{v}</span>
                              <span className="text-muted-foreground">
                                ({((v / row.total) * 100).toFixed(0)}%)
                              </span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <History className="size-4" /> Scan-Verlauf pro Konto
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.isLoading ? (
            <p className="text-sm text-muted-foreground">Lade…</p>
          ) : !history.data || history.data.inboxes.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Noch keine Gmail-Konten oder keine Scan-Ergebnisse im gewählten Zeitraum.
            </p>
          ) : (
            <div className="space-y-6">
              {history.data.inboxes.map((ib) => (
                <div key={ib.inbox_id} className="rounded-lg border border-border">
                  <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 border-b border-border bg-muted/30">
                    <div>
                      <div className="text-sm font-medium">{ib.display_name || ib.email}</div>
                      <div className="text-xs text-muted-foreground">{ib.email}</div>
                    </div>
                    <div className="text-xs text-muted-foreground text-right">
                      <div>
                        Letzter Scan:{" "}
                        {ib.last_checked_at ? new Date(ib.last_checked_at).toLocaleString() : "—"}
                      </div>
                      {ib.last_error && <div className="text-rose-500 mt-0.5">{ib.last_error}</div>}
                    </div>
                  </div>
                  {ib.events.length === 0 ? (
                    <p className="px-4 py-3 text-xs text-muted-foreground">
                      Keine Ergebnisse im Zeitraum.
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-muted-foreground border-b border-border">
                            <th className="px-4 py-2 font-normal">Zeitpunkt</th>
                            <th className="px-4 py-2 font-normal">Placement</th>
                            <th className="px-4 py-2 font-normal">Absender</th>
                            <th className="px-4 py-2 font-normal">Betreff</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ib.events.map((ev, idx) => (
                            <tr key={idx} className="border-b border-border last:border-0">
                              <td className="px-4 py-2 tabular-nums whitespace-nowrap">
                                {new Date(ev.detected_at).toLocaleString()}
                              </td>
                              <td className="px-4 py-2">
                                <span className="inline-flex items-center gap-1.5">
                                  <span
                                    className={`size-2 rounded-full ${PLACEMENT_LABELS[ev.placement]?.color ?? "bg-zinc-500"}`}
                                  />
                                  {PLACEMENT_LABELS[ev.placement]?.label ?? ev.placement}
                                </span>
                              </td>
                              <td
                                className="px-4 py-2 text-muted-foreground max-w-[220px] truncate"
                                title={ev.raw_from ?? ev.sending_domain ?? ""}
                              >
                                {ev.raw_from ?? ev.sending_domain ?? "—"}
                              </td>
                              <td
                                className="px-4 py-2 max-w-[360px] truncate"
                                title={ev.raw_subject ?? ""}
                              >
                                {ev.raw_subject ?? "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
